'use client';

import { twMerge } from 'tailwind-merge';
import { Container } from '../Container/Container';
import { Heading } from '../Heading/Heading';
import { Text } from '../Text/Text';
import { RegulationDisclaimer } from './components/RegulationDisclaimer';
import { firstNonEmpty } from '@/lib/strings/firstNonEmpty';
import { FishingWaterRegionFields, PlaceRegionFields } from '@/generated/graphql';
import { Boundary } from '@/api/fishrules/getBoundariesAtLocation';
import { ICoordinates } from '@/types/geo';
import { SpeciesRegulations } from '@/api/fishrules/getSpeciesRegulationsAtLocation';
import { LicenseSection } from './components/LicenseSection';
import { StaticPlaceMap } from '../StaticPlaceMap/StaticPlaceMap';
import { ContinuousCarousel } from '../Carousels/ContinuousCarousel/ContinuousCarousel';
import { RegulationCardNew } from '../RegulationCardNew/RegulationCardNew';
import { accordionTitle } from '../RegulationCardNew/helpers/buildAccordionData';
import { isProhibitedLocation } from '@domain/regulations/isProhibitedLocation';
import { getSeasonBadge } from './helpers/getSeasonBadge';
import { ProhibitionWarning } from './components/ProhibitionWarning';
import { SeeMoreSpeciesCTA } from './components/SeeMoreSpeciesCTA';

export type IRegulationSectionNewProps = {
  coordinates: ICoordinates;
  boundariesAtLocation?: Boundary[];
  initialRegulations?: SpeciesRegulations;
  speciesRegulations?: SpeciesRegulations[];
  speciesIdsFilter?: string[];
  region: FishingWaterRegionFields | PlaceRegionFields | undefined;
  title?: string;
  className?: string;
  showStaticMap?: boolean;
  showLicenceInfo?: boolean;
  id?: string;
  enableNewVariant?: boolean;
  appDownloadModalTitle?: string;
  licenseUrl?: string;
};

export const RegulationSectionNew = ({
  coordinates,
  id = 'regulations',
  title = 'Regulations',
  showStaticMap = false,
  showLicenceInfo = false,
  className,
  region,
  speciesRegulations = [],
  boundariesAtLocation,
  appDownloadModalTitle,
  licenseUrl,
}: IRegulationSectionNewProps) => {
  const resolvedLicenseUrl = licenseUrl ?? region?.licenseInfoUrl ?? undefined;
  const now = new Date();
  const { isProhibited: isLocationProhibited, message: locationProhibitedMessage } =
    isProhibitedLocation(boundariesAtLocation ?? []);
  const prohibitionMessage =
    locationProhibitedMessage && locationProhibitedMessage.length > 0
      ? locationProhibitedMessage
      : 'Fishing is prohibited at this location.';
  const firstBoundaryName = speciesRegulations[0]?.regulations.location_name;

  return (
    <div
      className="bg-lake-fog scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset)"
      id={id}
      data-testid="RegulationsNew"
    >
      <Container className={twMerge('py-sm', className)}>
        <Heading as="h2" variant="sm" className="mb-8">
          {title}
        </Heading>
        <div className="grid grid-cols-4 gap-3 md:grid-cols-12 md:gap-4">
          <div className="order-2 col-span-4 md:order-1 md:col-span-6">
            <RegulationDisclaimer />
            <Text>
              Fishing regulations{' '}
              {region ? `in ${firstNonEmpty(region.localName, region.name)}` : ''} can change
              throughout the year. Make sure to check this page before fishing for the most up to
              date rules and regulations for the current season. Local regulations govern when you
              can fish, the max size of the fish you can keep, how many fish you can keep, and more.
            </Text>
          </div>
          <div className="order-1 col-span-4 col-start-1 mb-3 sm:col-span-3 sm:mb-8 md:top-0 md:order-2 md:col-span-5 md:col-start-8">
            {showLicenceInfo && region && resolvedLicenseUrl && (
              <LicenseSection
                className="hidden bg-white md:mx-0 md:mt-8 md:flex md:max-w-none"
                region={region}
                licenseUrl={resolvedLicenseUrl}
              />
            )}
            {showStaticMap && <StaticPlaceMap coordinates={coordinates} />}
          </div>
        </div>
        {isLocationProhibited ? (
          <div
            className="mt-10 flex flex-col gap-4 md:mt-16"
            data-testid="RegulationLocationProhibited"
          >
            {firstBoundaryName && (
              <div className="flex w-full items-center gap-1 rounded-lg p-0 leading-normal">
                {accordionTitle('Regulation boundary', firstBoundaryName)}
              </div>
            )}
            <ProhibitionWarning message={prohibitionMessage} />
          </div>
        ) : (
          speciesRegulations.length > 0 && (
            <ContinuousCarousel
              title="Regulations for top species"
              headingVariant="sm"
              headingClassName="constantSize"
              outerClassName="mt-10! md:mt-16!"
              innerClassName="mb-6 sm:mb-6"
              dataTestId="RegulationCardCarousel"
              childrenClassName="w-[85%] min-w-[85%] sm:w-col-6 sm:min-w-col-6 md:flex-1 md:min-w-col-4 md:w-col-4"
            >
              {speciesRegulations.map(entry => (
                <RegulationCardNew
                  key={entry.regulations.fish_id}
                  speciesRegulations={entry}
                  seasonBadge={getSeasonBadge(entry.regulations.seasons, now)}
                  boundaries={boundariesAtLocation}
                  className="h-full"
                />
              ))}
            </ContinuousCarousel>
          )
        )}
        {appDownloadModalTitle && (
          <div className="mt-8 flex justify-center md:mt-12">
            <SeeMoreSpeciesCTA
              appDownloadModalTitle={appDownloadModalTitle}
              label={
                isLocationProhibited || speciesRegulations.length === 0
                  ? 'Check regulations in the app'
                  : 'See more species'
              }
            />
          </div>
        )}
      </Container>
      {showLicenceInfo && region && resolvedLicenseUrl && (
        <Container className="mb-md sm:mt-md bg-white py-12 md:hidden">
          <LicenseSection region={region} licenseUrl={resolvedLicenseUrl} />
        </Container>
      )}
    </div>
  );
};
